/** Normalizes session API fields (camelCase from server interceptor + legacy snake_case). */

export function getSessionTimeslots(session: any) {
  const raw =
    session?.sessionTimeslot ??
    session?.sessionTimeslots ??
    session?.session_timeslot ??
    [];

  return (Array.isArray(raw) ? raw : []).map((st: any) => ({
    ...st,
    timeslot: st.timeslot ?? st.timeSlots ?? st.time_slots,
    timeslotId:
      st.timeslotId ??
      st.timeslot_id ??
      st.timeSlots?.id ??
      st.time_slots?.id,
  }));
}

export function getPayTabs(session: any) {
  const raw = session?.payTabs ?? session?.pay_tabs ?? null;
  if (!raw) return null;
  return {
    ...raw,
    paymentStatus: raw.paymentStatus ?? raw.payment_status,
    refundStatus: raw.refundStatus ?? raw.refund_status,
    transactionRef: raw.transactionRef ?? raw.transaction_ref,
    cartId: raw.cartId ?? raw.cart_id,
    amount: raw.amount != null ? Number(raw.amount) : null,
    currency: raw.currency ?? "SAR",
    paymentMethod: raw.paymentMethod ?? raw.payment_method,
    cardScheme: raw.cardScheme ?? raw.card_scheme,
    cardType: raw.cardType ?? raw.card_type,
    maskedPan: raw.maskedPan ?? raw.masked_pan,
    responseMessage: raw.responseMessage ?? raw.response_message,
    refundAmount: raw.refundAmount ?? raw.refund_amount,
    refundRef: raw.refundRef ?? raw.refund_ref,
  };
}

export function getSessionPayment(session: any) {
  const raw = session?.sessionPayment ?? session?.session_payment ?? null;
  if (!raw) return null;

  const amount =
    raw.amount != null
      ? Number(raw.amount)
      : raw.totalAmount != null
        ? Number(raw.totalAmount)
        : raw.total_amount != null
          ? Number(raw.total_amount)
          : null;

  const coupon = raw.coupon ?? null;
  const couponCode =
    raw.couponCode ?? raw.coupon_code ?? coupon?.code ?? null;

  return {
    ...raw,
    amount,
    totalAmount: amount,
    currency: raw.currency ?? "SAR",
    status: raw.status ?? null,
    paymentMethod: raw.paymentMethod ?? raw.payment_method ?? null,
    transactionId: raw.transactionId ?? raw.transaction_id ?? null,
    paymentReference: raw.paymentReference ?? raw.payment_reference ?? null,
    paidAt: raw.paidAt ?? raw.paid_at ?? null,
    invoiceNumber: raw.invoiceNumber ?? raw.invoice_number ?? null,
    invoiceUrl: raw.invoiceUrl ?? raw.invoice_url ?? null,
    originalSubtotal:
      raw.originalSubtotal != null
        ? Number(raw.originalSubtotal)
        : raw.original_subtotal != null
          ? Number(raw.original_subtotal)
          : null,
    discountAmount:
      raw.discountAmount != null
        ? Number(raw.discountAmount)
        : raw.discount_amount != null
          ? Number(raw.discount_amount)
          : 0,
    discountedSubtotal:
      raw.discountedSubtotal != null
        ? Number(raw.discountedSubtotal)
        : raw.discounted_subtotal != null
          ? Number(raw.discounted_subtotal)
          : null,
    vatPercentage:
      raw.vatPercentage != null
        ? Number(raw.vatPercentage)
        : raw.vat_percentage != null
          ? Number(raw.vat_percentage)
          : null,
    vatAmount:
      raw.vatAmount != null
        ? Number(raw.vatAmount)
        : raw.vat_amount != null
          ? Number(raw.vat_amount)
          : null,
    couponId: raw.couponId ?? raw.coupon_id ?? coupon?.id ?? null,
    couponCode,
    coupon: coupon
      ? {
          id: coupon.id,
          code: coupon.code,
          title:
            coupon.title ??
            coupon.titleEn ??
            coupon.title_en ??
            null,
          type: coupon.type,
          value: coupon.value != null ? Number(coupon.value) : null,
          fundedBy: coupon.fundedBy ?? coupon.funded_by ?? null,
          isActive: coupon.isActive ?? coupon.is_active ?? null,
        }
      : null,
  };
}

export function formatMoney(amount: number | null | undefined, currency = "SAR") {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  return `${Number(amount).toFixed(2)} ${currency}`;
}

export function getSessionClinicianRecord(session: any) {
  const record = session?.sessionClinician ?? session?.session_clinician;
  if (Array.isArray(record)) {
    return record[0] ?? null;
  }
  return record ?? null;
}

export function getHandoverFlags(session: any) {
  const sc = getSessionClinicianRecord(session);
  return {
    sessionClinicianId: sc?.id ?? 0,
    isNewClinicianAccepted:
      sc?.isNewClinicianAccepted ?? sc?.is_accepted ?? null,
    isPatientAccepted:
      sc?.isPatientAccepted ?? sc?.is_patient_accepted ?? null,
    hasPendingHandover: Boolean(sc?.newClinicianId ?? sc?.new_clinician_id),
  };
}

export function getPrimarySessionDate(session: any) {
  const slots = getSessionTimeslots(session);
  return slots[0]?.date ?? session?.session_date ?? session?.date;
}

export function getConsultationMode(session: any) {
  return session?.consultationMode ?? session?.consultation_mode ?? "";
}

export function isVirtualConsultation(session: any) {
  const mode = getConsultationMode(session);
  return mode === "AUDIO" || mode === "VIDEO";
}

/** API nests login identity under `users` (Prisma); some clients used `user`. */
export function getNestedUser(entity: any) {
  return entity?.users ?? entity?.user ?? null;
}

export function getUserFullName(entity: any): string {
  const user = getNestedUser(entity);
  return (
    user?.fullName ||
    user?.full_name ||
    entity?.fullName ||
    entity?.full_name ||
    ""
  );
}

export function getUserEmail(entity: any): string {
  const user = getNestedUser(entity);
  return user?.email || entity?.email || "";
}

export function getUserMobile(entity: any): string {
  const user = getNestedUser(entity);
  return (
    user?.mobileNumber ||
    user?.mobile_number ||
    entity?.mobileNumber ||
    entity?.mobile_number ||
    entity?.phone ||
    ""
  );
}

/** Account holder (patients row) + clinical profile for a session. */
export function getSessionPatientParties(session: any) {
  const accountHolder =
    session?.patient ?? session?.patients ?? null;
  const profile =
    session?.patientProfile ??
    session?.patient_profile ??
    session?.patientProfiles ??
    session?.patient_profiles ??
    null;

  return {
    accountHolder,
    accountHolderName: getUserFullName(accountHolder),
    accountHolderEmail: getUserEmail(accountHolder) || "N/A",
    accountHolderMobile: getUserMobile(accountHolder) || "N/A",
    accountHolderImage: accountHolder?.imageUrl || accountHolder?.image_url || "",
    profile,
    profileName: profile?.fullName || profile?.full_name || "",
    profileAge: profile?.age ?? 0,
    profileGender: profile?.gender || "",
    profileImage: profile?.imageUrl || profile?.image_url || "",
    profileRelation: profile?.relation || "",
  };
}

/** Prefer session_clinician.clinician, fall back to session.clinician / clinicians. */
export function getSessionClinicianParty(session: any) {
  const record = getSessionClinicianRecord(session);
  const clinician =
    record?.clinician ??
    record?.clinicians ??
    session?.clinician ??
    session?.clinicians ??
    null;

  const user = getNestedUser(clinician);
  return {
    clinician,
    newClinician: record?.newClinician ?? record?.new_clinician ?? null,
    name: getUserFullName(clinician),
    email: getUserEmail(clinician) || "N/A",
    mobile: getUserMobile(clinician) || "N/A",
    imageUrl: clinician?.imageUrl || clinician?.image_url || "",
    designation: clinician?.designation || "",
    experience: clinician?.experience != null ? String(clinician.experience) : "",
    license: clinician?.license || "",
    location: clinician?.location || "",
    languages:
      clinician?.languagesKnown ||
      clinician?.languages_known ||
      "",
    user,
  };
}
